import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Plus, X } from 'lucide-react'
import { deliveriesAPI } from '../../api/deliveries'
import { usersAPI } from '../../api/users'
import Modal from '../ui/Modal'
import { ColisQRCode } from './ColisQRCode'

const G = '#1D9E75'
const TVA = 0.19

const SIZES = [
  { value: 'small',  label: 'Petit' },
  { value: 'medium', label: 'Moyen' },
  { value: 'large',  label: 'Grand' },
  { value: 'extra',  label: 'Extra-large' },
] as const

const STEP_LABELS = ['Destinataire', 'Colis', 'Paiement', 'QR Code']

interface ProductItem {
  name: string
  priceHT: string
  priceTTC: string
  qty: string
}

const BLANK_PRODUCT: ProductItem = { name: '', priceHT: '', priceTTC: '', qty: '1' }

interface FormState {
  contactName: string
  contactPhone: string
  dropoffAddress: string
  notes: string
  products: ProductItem[]
  size: 'small' | 'medium' | 'large' | 'extra'
  weight: string
  fragile: boolean
  paymentMethod: 'cash' | 'online'
}

const BLANK: FormState = {
  contactName: '', contactPhone: '', dropoffAddress: '', notes: '',
  products: [{ ...BLANK_PRODUCT }],
  size: 'medium', weight: '', fragile: false,
  paymentMethod: 'cash',
}

function validate(step: number, f: FormState): Record<string, string> {
  const e: Record<string, string> = {}
  if (step === 1) {
    if (f.contactName.trim().length < 2)    e.contactName    = 'Minimum 2 caractères'
    if (!/^\d{8}$/.test(f.contactPhone))    e.contactPhone   = '8 chiffres requis'
    if (f.dropoffAddress.trim().length < 5) e.dropoffAddress = 'Minimum 5 caractères'
    if (f.notes.length > 300)               e.notes          = 'Maximum 300 caractères'
  }
  if (step === 2) {
    if (f.products.length === 0) {
      e.products = 'Ajoutez au moins un produit'
    } else {
      f.products.forEach((p, i) => {
        if (!p.name.trim())                                      e[`p_${i}_name`] = 'Nom requis'
        const ht = parseFloat(p.priceHT)
        if (isNaN(ht) || ht <= 0)                               e[`p_${i}_ht`]   = 'Prix invalide'
        const qty = parseInt(p.qty)
        if (isNaN(qty) || qty < 1)                              e[`p_${i}_qty`]  = 'Qté invalide'
      })
    }
    if (f.weight && (isNaN(parseFloat(f.weight)) || parseFloat(f.weight) <= 0))
      e.weight = 'Poids invalide'
  }
  return e
}

function StepDot({ n, current }: { n: number; current: number }) {
  const done = current > n
  const active = current === n
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: done || active ? G : '#f3f4f6',
      color: done || active ? '#fff' : '#9ca3af',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700,
    }}>
      {done ? <Check size={14} /> : n}
    </div>
  )
}

function fmt(n: number) {
  return n.toFixed(3)
}

interface Props { onClose: () => void }

export default function AddCommandeModal({ onClose }: Props) {
  const qc = useQueryClient()
  const [step, setStep]             = useState(1)
  const [form, setForm]             = useState<FormState>(BLANK)
  const [errs, setErrs]             = useState<Record<string, string>>({})
  const [apiErr, setApiErr]         = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [createdDelivery, setCreatedDelivery] = useState<{ _id: string; trackingCode: string } | null>(null)

  const { data: meData } = useQuery({
    queryKey: ['me-profile'],
    queryFn: () => usersAPI.getMe(),
  })
  const fraisLivraison: number = (meData as any)?.fraisLivraison ?? 0

  function patch<K extends keyof FormState>(key: K, val: FormState[K]) {
    setForm((f) => ({ ...f, [key]: val }))
    setErrs((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== (key as string))))
  }

  const patchProduct = useCallback((i: number, field: keyof ProductItem, value: string) => {
    setForm((f) => {
      const products = f.products.map((p, idx) => {
        if (idx !== i) return p
        const updated = { ...p, [field]: value }
        if (field === 'priceHT') {
          const ht = parseFloat(value)
          if (!isNaN(ht) && ht > 0) updated.priceTTC = fmt(ht * (1 + TVA))
        }
        if (field === 'priceTTC') {
          const ttc = parseFloat(value)
          if (!isNaN(ttc) && ttc > 0) updated.priceHT = fmt(ttc / (1 + TVA))
        }
        return updated
      })
      return { ...f, products }
    })
    setErrs((prev) => {
      const next = { ...prev }
      delete next[`p_${i}_name`]
      delete next[`p_${i}_ht`]
      delete next[`p_${i}_qty`]
      return next
    })
  }, [])

  const addProduct    = () => setForm((f) => ({ ...f, products: [...f.products, { ...BLANK_PRODUCT }] }))
  const removeProduct = (i: number) => setForm((f) => ({ ...f, products: f.products.filter((_, idx) => idx !== i) }))

  function next() {
    const e = validate(step, form)
    setErrs(e)
    if (Object.keys(e).length) return
    setStep((s) => s + 1)
  }

  // Totals
  const totalHT  = form.products.reduce((s, p) => s + (parseFloat(p.priceHT) || 0) * (parseInt(p.qty) || 0), 0)
  const totalTTC = form.products.reduce((s, p) => s + (parseFloat(p.priceTTC) || 0) * (parseInt(p.qty) || 0), 0)
  const grandTotal = totalTTC + fraisLivraison

  async function submit() {
    setApiErr('')
    setSubmitting(true)
    try {
      const products = form.products.map((p) => ({
        name: p.name.trim(),
        priceHT: parseFloat(p.priceHT) || 0,
        priceTTC: parseFloat(p.priceTTC) || 0,
        quantity: parseInt(p.qty) || 1,
      }))
      const res = await deliveriesAPI.create({
        dropoff: {
          address: form.dropoffAddress,
          contactName: form.contactName,
          contactPhone: `+216${form.contactPhone}`,
          ...(form.notes ? { notes: form.notes } : {}),
        },
        package: {
          size: form.size,
          fragile: form.fragile,
          ...(form.weight ? { weight: parseFloat(form.weight) } : {}),
        },
        products,
        paymentMethod: form.paymentMethod,
      })
      const delivery = (res as any).data?.delivery ?? (res as any).data
      setCreatedDelivery({ _id: delivery._id, trackingCode: delivery.trackingCode })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['expediteurDeliveries'] }),
        qc.invalidateQueries({ queryKey: ['deliveries'] }),
        qc.invalidateQueries({ queryKey: ['expediteur-stats'] }),
      ])
      setStep(4)
    } catch (err) {
      const msg = (err as any).response?.data?.message ?? 'Une erreur est survenue'
      setApiErr(msg)
    } finally {
      setSubmitting(false)
    }
  }

  function handleClose() {
    setStep(1); setForm(BLANK); setCreatedDelivery(null); setApiErr(''); onClose()
  }

  // Shared styles
  const inp: React.CSSProperties = {
    width: '100%', padding: '9px 11px', borderRadius: 8,
    border: '1px solid #e5e7eb', fontSize: 13,
    outline: 'none', boxSizing: 'border-box', background: '#fff',
  }
  const inpSm: React.CSSProperties = { ...inp, padding: '7px 9px' }
  const lbl: React.CSSProperties  = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }
  const errS: React.CSSProperties = { color: '#dc2626', fontSize: 11, marginTop: 3 }
  const line: React.CSSProperties = { borderTop: '1px solid #f3f4f6', margin: '6px 0' }

  return (
    <Modal open onClose={handleClose} title="Nouvelle commande" size="md">

      {/* Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <StepDot n={1} current={step} />
        <div style={{ flex: 1, height: 2, background: step > 1 ? G : '#e5e7eb', margin: '0 4px' }} />
        <StepDot n={2} current={step} />
        <div style={{ flex: 1, height: 2, background: step > 2 ? G : '#e5e7eb', margin: '0 4px' }} />
        <StepDot n={3} current={step} />
        <div style={{ flex: 1, height: 2, background: step > 3 ? G : '#e5e7eb', margin: '0 4px' }} />
        <StepDot n={4} current={step} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 22 }}>
        {STEP_LABELS.map((label, i) => (
          <span key={label} style={{ fontSize: 11, color: step === i + 1 ? G : '#9ca3af', fontWeight: step === i + 1 ? 600 : 400 }}>
            {label}
          </span>
        ))}
      </div>

      {/* ── Step 1: Destinataire ── */}
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div>
            <label style={lbl}>Nom du destinataire <span style={{ color: '#dc2626' }}>*</span></label>
            <input style={errs.contactName ? { ...inp, borderColor: '#fca5a5' } : inp}
              value={form.contactName}
              onChange={(e) => patch('contactName', e.target.value)}
              placeholder="Ahmed Ben Ali" />
            {errs.contactName && <p style={errS}>{errs.contactName}</p>}
          </div>
          <div>
            <label style={lbl}>Téléphone <span style={{ color: '#dc2626' }}>*</span></label>
            <div style={{ display: 'flex' }}>
              <span style={{ padding: '9px 10px 9px 12px', borderRadius: '8px 0 0 8px', border: '1px solid #e5e7eb', borderRight: 'none', background: '#f9fafb', fontSize: 13, color: '#6b7280' }}>
                +216
              </span>
              <input
                style={{ ...inp, borderRadius: '0 8px 8px 0', ...(errs.contactPhone ? { borderColor: '#fca5a5' } : {}) }}
                value={form.contactPhone}
                onChange={(e) => patch('contactPhone', e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="20123456" inputMode="numeric" />
            </div>
            {errs.contactPhone && <p style={errS}>{errs.contactPhone}</p>}
          </div>
          <div>
            <label style={lbl}>Adresse de livraison <span style={{ color: '#dc2626' }}>*</span></label>
            <input style={errs.dropoffAddress ? { ...inp, borderColor: '#fca5a5' } : inp}
              value={form.dropoffAddress}
              onChange={(e) => patch('dropoffAddress', e.target.value)}
              placeholder="Rue de la République, Tunis" />
            {errs.dropoffAddress && <p style={errS}>{errs.dropoffAddress}</p>}
          </div>
          <div>
            <label style={lbl}>Notes <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optionnel)</span></label>
            <textarea
              style={{ ...inp, resize: 'vertical', minHeight: 72, ...(errs.notes ? { borderColor: '#fca5a5' } : {}) }}
              value={form.notes}
              onChange={(e) => patch('notes', e.target.value)}
              placeholder="Instructions pour le livreur…" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 11, color: form.notes.length > 300 ? '#dc2626' : '#9ca3af' }}>
              {form.notes.length}/300
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Colis (Products + Package) ── */}
      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Products */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Produits <span style={{ color: '#dc2626' }}>*</span></label>
              <button type="button" onClick={addProduct}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: `1px solid ${G}`, background: G + '12', color: G, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Plus size={12} /> Ajouter
              </button>
            </div>
            {errs.products && <p style={errS}>{errs.products}</p>}

            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 56px 28px', gap: 6, marginBottom: 5 }}>
              {['Produit', 'Prix HT', 'Prix TTC', 'Qté', ''].map((h) => (
                <span key={h} style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</span>
              ))}
            </div>

            {form.products.map((p, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 56px 28px', gap: 6, marginBottom: 7 }}>
                <div>
                  <input
                    style={{ ...inpSm, ...(errs[`p_${i}_name`] ? { borderColor: '#fca5a5' } : {}) }}
                    value={p.name}
                    onChange={(e) => patchProduct(i, 'name', e.target.value)}
                    placeholder="Nom du produit" />
                  {errs[`p_${i}_name`] && <p style={errS}>{errs[`p_${i}_name`]}</p>}
                </div>
                <div>
                  <input
                    style={{ ...inpSm, ...(errs[`p_${i}_ht`] ? { borderColor: '#fca5a5' } : {}) }}
                    type="number" min="0" step="0.001"
                    value={p.priceHT}
                    onChange={(e) => patchProduct(i, 'priceHT', e.target.value)}
                    placeholder="0.000" />
                  {errs[`p_${i}_ht`] && <p style={errS}>{errs[`p_${i}_ht`]}</p>}
                </div>
                <input
                  style={inpSm}
                  type="number" min="0" step="0.001"
                  value={p.priceTTC}
                  onChange={(e) => patchProduct(i, 'priceTTC', e.target.value)}
                  placeholder="0.000" />
                <div>
                  <input
                    style={{ ...inpSm, ...(errs[`p_${i}_qty`] ? { borderColor: '#fca5a5' } : {}) }}
                    type="number" min="1" step="1"
                    value={p.qty}
                    onChange={(e) => patchProduct(i, 'qty', e.target.value)}
                    placeholder="1" />
                  {errs[`p_${i}_qty`] && <p style={errS}>{errs[`p_${i}_qty`]}</p>}
                </div>
                <button type="button" onClick={() => removeProduct(i)}
                  disabled={form.products.length === 1}
                  style={{
                    height: 33, width: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 6, border: '1px solid #fee2e2',
                    background: form.products.length === 1 ? '#f9fafb' : '#fff',
                    color: form.products.length === 1 ? '#d1d5db' : '#ef4444',
                    cursor: form.products.length === 1 ? 'default' : 'pointer',
                  }}>
                  <X size={12} />
                </button>
              </div>
            ))}

            {/* Running totals */}
            {totalHT > 0 && (
              <div style={{ marginTop: 4, padding: '8px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6', fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280' }}>
                  <span>Sous-total HT</span>
                  <span style={{ fontWeight: 600 }}>{fmt(totalHT)} TND</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280', marginTop: 3 }}>
                  <span>TVA (19%)</span>
                  <span style={{ fontWeight: 600 }}>{fmt(totalTTC - totalHT)} TND</span>
                </div>
                <div style={line} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Total TTC</span>
                  <span style={{ fontWeight: 700, color: G }}>{fmt(totalTTC)} TND</span>
                </div>
              </div>
            )}
          </div>

          <div style={line} />

          {/* Package attributes */}
          <div>
            <label style={lbl}>Taille du colis <span style={{ color: '#dc2626' }}>*</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {SIZES.map(({ value, label }) => (
                <button key={value} type="button" onClick={() => patch('size', value)}
                  style={{
                    padding: '8px 6px', borderRadius: 8, cursor: 'pointer',
                    border: `2px solid ${form.size === value ? G : '#e5e7eb'}`,
                    background: form.size === value ? G + '15' : '#fff',
                    color: form.size === value ? G : '#374151',
                    fontSize: 12, fontWeight: form.size === value ? 600 : 400,
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={lbl}>Poids (kg) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optionnel)</span></label>
              <input
                style={errs.weight ? { ...inp, borderColor: '#fca5a5' } : inp}
                type="number" min="0" step="0.1"
                value={form.weight}
                onChange={(e) => patch('weight', e.target.value)}
                placeholder="0.5" />
              {errs.weight && <p style={errS}>{errs.weight}</p>}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#374151' }}>
                <button type="button" onClick={() => patch('fragile', !form.fragile)}
                  style={{ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', background: form.fragile ? G : '#d1d5db', position: 'relative', flexShrink: 0 }}>
                  <span style={{ position: 'absolute', top: 2, left: form.fragile ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                </button>
                Fragile
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Paiement ── */}
      {step === 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Price breakdown */}
          <div style={{ padding: '14px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6', fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af' }}>
              Détail du montant
            </div>

            {form.products.map((p, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#374151' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
                  {p.name || `Produit ${i + 1}`} <span style={{ color: '#9ca3af' }}>× {p.qty}</span>
                </span>
                <span style={{ fontWeight: 500 }}>
                  {fmt((parseFloat(p.priceTTC) || 0) * (parseInt(p.qty) || 0))} TND
                </span>
              </div>
            ))}

            <div style={line} />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#6b7280' }}>
              <span>Sous-total HT</span>
              <span>{fmt(totalHT)} TND</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#6b7280' }}>
              <span>TVA (19%)</span>
              <span>{fmt(totalTTC - totalHT)} TND</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#6b7280' }}>
              <span>Frais de livraison</span>
              <span style={{ color: '#374151', fontWeight: 500 }}>+{fmt(fraisLivraison)} TND</span>
            </div>

            <div style={{ ...line, margin: '8px 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: '#0d1117' }}>Total à payer</span>
              <span style={{ color: G }}>{fmt(grandTotal)} TND</span>
            </div>
          </div>

          {/* Payment method */}
          <div>
            <label style={lbl}>Mode de paiement <span style={{ color: '#dc2626' }}>*</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {([
                ['cash',   '💵 Espèces',  'Paiement à la livraison'],
                ['online', '💳 En ligne', 'Carte / Virement'],
              ] as const).map(([val, title, sub]) => (
                <button key={val} type="button" onClick={() => patch('paymentMethod', val)}
                  style={{
                    padding: '13px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                    border: `2px solid ${form.paymentMethod === val ? G : '#e5e7eb'}`,
                    background: form.paymentMethod === val ? G + '08' : '#fff',
                  }}>
                  <div style={{ fontSize: 14, marginBottom: 3 }}>{title}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>{sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Récapitulatif */}
          <div style={{ padding: '12px 14px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', marginBottom: 8 }}>
              Récapitulatif
            </div>
            {[
              ['Destinataire', form.contactName || '—'],
              ['Téléphone',    form.contactPhone ? `+216 ${form.contactPhone}` : '—'],
              ['Adresse',      form.dropoffAddress || '—'],
              ['Taille',       SIZES.find((s) => s.value === form.size)?.label ?? '—'],
              ['Fragile',      form.fragile ? 'Oui' : 'Non'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: '#6b7280' }}>{k}</span>
                <span style={{ fontWeight: 500, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Step 4: QR Code ── */}
      {step === 4 && createdDelivery && (
        <div style={{ padding: '8px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(29,158,117,0.08)', border: '1px solid rgba(29,158,117,0.25)',
            borderRadius: 12, padding: '12px 16px', marginBottom: 24,
          }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div style={{ color: G, fontWeight: 600, fontSize: 14 }}>Colis créé avec succès !</div>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Scannez ou imprimez le QR code pour coller sur le colis</div>
            </div>
          </div>
          <ColisQRCode
            trackingCode={createdDelivery.trackingCode}
            contactName={form.contactName}
            dropoffAddress={form.dropoffAddress}
            price={grandTotal}
            paymentMethod={form.paymentMethod}
            size={200}
            showActions={true}
          />
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button onClick={handleClose}
              style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, color: '#374151', fontSize: 14, fontWeight: 500, padding: '10px 32px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* API error */}
      {apiErr && step < 4 && (
        <div style={{ marginTop: 14, padding: '10px 14px', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
          {apiErr}
        </div>
      )}

      {/* Navigation */}
      {step < 4 && (
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button type="button" onClick={step > 1 ? () => setStep((s) => s - 1) : handleClose}
            style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
            {step > 1 ? '← Retour' : 'Annuler'}
          </button>
          {step < 3 ? (
            <button type="button" onClick={next}
              style={{ flex: 2, padding: 10, borderRadius: 8, border: 'none', background: G, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              Suivant →
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={submitting}
              style={{ flex: 2, padding: 10, borderRadius: 8, border: 'none', background: submitting ? '#9ca3af' : G, color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {submitting ? (
                <>
                  <div style={{ width: 15, height: 15, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                  Création en cours…
                </>
              ) : 'Créer la commande'}
            </button>
          )}
        </div>
      )}
    </Modal>
  )
}
